import { Injectable } from '@angular/core';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DelayLinkType, StepInstance,
  TimeDuration,
  TypeSwitch
} from '../../../models/projects/operation.models';
import { OperationService } from '../../operations/operation/operation.service';
import { DateParserService } from '../../daily-log/daily-log-time/date-parser-service';
import { StepService } from '../../step/step.service';
import { DateService } from '../../date/date.service';
import { DailyLogService } from '../../daily-log/daily-log.service';
import { cloneDeep } from 'lodash';
import { AppDataSyncService } from '../../app-data-sync/app-data-sync.service';
import { AppData, DelaySaveHelper } from '../../../models/app-data/app-data';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { TransitingSaveService } from '../../operations/transiting-save/transiting-save.service';
import { HttpClient } from '@angular/common/http';
import { OperationSaveService } from '../../operations/operation-save/operation-save.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class DelayService {

  config = AppConfig.settings;

  constructor(
    private operationService: OperationService,
    private dateParserService: DateParserService,
    private stepService: StepService,
    private dateService: DateService,
    private dailyLogService: DailyLogService,
    private appDataSyncService: AppDataSyncService,
    private transitingSaveService: TransitingSaveService,
    private http: HttpClient,
    private operationSaveService: OperationSaveService,
    private timeZoneService: TimeZoneService,
  ) {
  }

  async saveDelay(delaySaveHelper: DelaySaveHelper): Promise<AppData> {
    const url = `${this.config.apiUrl}api/appdata/savedelay`;
    let saveHelperClone = cloneDeep(delaySaveHelper) as DelaySaveHelper;

    if (!saveHelperClone.dailyLogDate) {
      saveHelperClone.dailyLogDate = (await this.dailyLogService.getCurrentLogDay()).dateTime;
    }

    // remove local offsets before sending to api
    saveHelperClone = this.dateParserService.removeOffsetFromLuxonDates(saveHelperClone);

    return this.http
      .post<DelaySaveHelper>(url, saveHelperClone)
      .toPromise()
      .then(async (data: any) => {
        // note we dont deserialize dates here because we dont need to
        return data;
      });
  }

  async findPotentiallyAffectedOperations(instance: DailyLogOperationInstance, dailyLogDate: PtLuxonDate): Promise<DailyLogOperationInstance[]> {
    if (!instance) {
      return null;
    }

    if (!instance.endTime || !instance.endTime.dateTime) {
      const endOfDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);
      instance.endTime = endOfDay;
    }

    let operationInstances = await this.operationService.getAllProjectSpecificOperationInstances(null);

    operationInstances = operationInstances.filter(value => value.id !== instance.id);

    return operationInstances
      .filter(p => {
        let newEndTime = cloneDeep(p.endTime.getDate());

        if (!!p.cancelledTime && (!p.cancelId || p.cancelId !== instance.id)) {
          newEndTime = cloneDeep(p.cancelledTime);
        }

        let found = this.operationService.operationInTime(
          p.startTime.dateTime,
          newEndTime,
          instance.startTime.dateTime,
          instance.endTime.getDate());

        if (!found) {
          found = this.operationService.operationInTime(
            instance.startTime.dateTime,
            instance.endTime.dateTime,
            p.startTime.dateTime,
            newEndTime);
        }

        return found;
      });
  }

  // at delay start, get/create startSwitch, that switches to concurrent
  // split steps at delay start if needed
  // if delay has end, get/create endSwitch, that switches back to contractual
  // split steps at delay end if needed
  async switchOperation(
    delay: DailyLogOperationInstance,
    operationToSwitch: DailyLogOperationInstance,
    existingDelayHelper: DelaySaveHelper
  ) {
    // initialise delayData if needed
    let delaySaveHelper = existingDelayHelper || new DelaySaveHelper();
    delaySaveHelper.dailyLogIdsToDelete = delaySaveHelper.dailyLogIdsToDelete || [];
    delaySaveHelper.operationIdsToDelete = delaySaveHelper.operationIdsToDelete || [];
    delaySaveHelper.switchedOperations = delaySaveHelper.switchedOperations || [];

    if (!operationToSwitch.switchTimes) {
      operationToSwitch.switchTimes = [];
    }

    // Ensure steps are split at the delay start
    await this.stepService.splitStepsAtTime(operationToSwitch, delay.startTime);

    // -------- Start switch (to concurrent) --------
    if (delay.startTime?.dateTime) {
      const existingStartSwitchIndex = operationToSwitch.switchTimes
        .findIndex(e => e.date.dateTime.toMillis() === delay.startTime.dateTime.toMillis());

      if (existingStartSwitchIndex === -1) {
        // if there is no switch with the same time, check for a switch with same delay id
        const delayIdSwitchStartIndex = operationToSwitch.switchTimes
          .findIndex(e => e.isConcurrent && e.delayApiId === delay.id && e.operationInstanceApiId === operationToSwitch.id);

        const useOpStartTime =
          delay.startTime.dateTime.toMillis() < operationToSwitch.startTime.dateTime.toMillis();

        if (delayIdSwitchStartIndex !== -1) {
          operationToSwitch.switchTimes[delayIdSwitchStartIndex].date = useOpStartTime ? operationToSwitch.startTime : delay.startTime;
          operationToSwitch.switchTimes[delayIdSwitchStartIndex].dateOffset = useOpStartTime ? operationToSwitch.startTimeOffset : delay.startTimeOffset;
          operationToSwitch.switchTimes[delayIdSwitchStartIndex].dateToShow = useOpStartTime ? operationToSwitch.startTimeToShow : delay.startTimeToShow;
        } else {
          const startTypeSwitch = new TypeSwitch();
          startTypeSwitch.date = useOpStartTime ? operationToSwitch.startTime : delay.startTime;
          startTypeSwitch.dateOffset = useOpStartTime ? operationToSwitch.startTimeOffset : delay.startTimeOffset;
          startTypeSwitch.delayApiId = delay.id;
          startTypeSwitch.operationInstanceApiId = operationToSwitch.id;
          startTypeSwitch.isConcurrent = true;
          startTypeSwitch.dateToShow = useOpStartTime ? operationToSwitch.startTimeToShow : delay.startTimeToShow;
          startTypeSwitch.guid = uuidv4();

          operationToSwitch.switchTimes.push(startTypeSwitch);
        }
      }
    }

    // -------- End switch (back to contractual) --------
    // Goal:
    //  - If delay ends AFTER a real op end => REMOVE any contractual end switch (don't add one).
    //  - Else (op has no end OR delay end <= op end) => ADD/UPDATE contractual end switch at delay end.
    const hasDelayEnd = !!(delay.endTime && delay.endTime.dateTime);
    const opHasEnd = !!(operationToSwitch.endTime && operationToSwitch.endTime.dateTime);

    if (hasDelayEnd) {
      const delayEndMs = delay.endTime.dateTime.toMillis();

      // (1) Remove if delay end is after a *real* operation end
      if (opHasEnd) {
        const opEndMs = operationToSwitch.endTime.dateTime.toMillis();
        if (delayEndMs > opEndMs) {
          const idxToRemove = operationToSwitch.switchTimes.findIndex(s =>
            !s.isConcurrent &&
            s.delayApiId === delay.id &&
            s.operationInstanceApiId === operationToSwitch.id
          );
          if (idxToRemove !== -1) {
            operationToSwitch.switchTimes.splice(idxToRemove, 1);
          }
          // Do not add a new end switch in this case.
        }
      }

      // (2) Add/Update end switch when op has no end, or delayEnd <= opEnd
      if (!opHasEnd || delayEndMs <= operationToSwitch.endTime.dateTime.toMillis()) {
        const sameTimeIdx = operationToSwitch.switchTimes.findIndex(e =>
          !e.isConcurrent &&
          e.date?.dateTime &&
          e.date.dateTime.toMillis() === delayEndMs &&
          e.delayApiId === delay.id &&
          e.operationInstanceApiId === operationToSwitch.id
        );

        if (sameTimeIdx === -1) {
          // Try update existing end switch for this delay/op
          const byDelayIdIdx = operationToSwitch.switchTimes.findIndex(e =>
            !e.isConcurrent && e.delayApiId === delay.id && e.operationInstanceApiId === operationToSwitch.id
          );

          if (byDelayIdIdx !== -1) {
            operationToSwitch.switchTimes[byDelayIdIdx].date = delay.endTime;
            operationToSwitch.switchTimes[byDelayIdIdx].dateOffset = delay.endTimeOffset;
            operationToSwitch.switchTimes[byDelayIdIdx].dateToShow = delay.endTimeToShow;
          } else {
            // Create a new contractual end switch
            const endTypeSwitch = new TypeSwitch();
            endTypeSwitch.date = delay.endTime;
            endTypeSwitch.dateOffset = delay.endTimeOffset;
            endTypeSwitch.delayApiId = delay.id;
            endTypeSwitch.operationInstanceApiId = operationToSwitch.id;
            endTypeSwitch.isConcurrent = false;
            endTypeSwitch.dateToShow = delay.endTimeToShow;
            endTypeSwitch.guid = uuidv4();

            operationToSwitch.switchTimes.push(endTypeSwitch);
          }
        }
      }
    }

    // After adding/removing switches, split any intersecting steps & stitch where possible
    await this.checkThirdStepSplitNeeded(operationToSwitch);

    operationToSwitch.delayLinkTypeId = DelayLinkType.Switched;
    delaySaveHelper.switchedOperations.push(operationToSwitch);

    delaySaveHelper = await this.saveAffectedOperationDailyLogRecordLogs(operationToSwitch, delaySaveHelper);

    return delaySaveHelper;
  }

  calculateSwitchId(switchTimes: TypeSwitch[]): number {
    if (!switchTimes || switchTimes.length < 1) {
      return 0;
    }

    let id = switchTimes[0].id;

    switchTimes.forEach(element => {
      if (element.id > id) {
        id = element.id;
      }
    });

    id++;

    return id;
  }

  calculatePauseId(pausedTimes: TimeDuration[], originalDelayCopy: DailyLogOperationInstance): number {
    const originalPauseIndex = originalDelayCopy && originalDelayCopy.startTime && originalDelayCopy.startTime.dateTime
      ? pausedTimes.findIndex(e => e.startDate.dateTime.toMillis() === originalDelayCopy.startTime.dateTime.toMillis())
      : null;

    if (originalPauseIndex !== null && originalPauseIndex !== -1) {
      return pausedTimes[originalPauseIndex].id;
    }

    if (!pausedTimes || pausedTimes.length < 1) {
      return 0;
    }

    let id = pausedTimes[0].id;

    pausedTimes.forEach(element => {
      if (element.id > id) {
        id = element.id;
      }
    });

    id++;

    return id;
  }

  public async cancelOperation(
    delay: DailyLogOperationInstance,
    operationToCancel: DailyLogOperationInstance,
    existingDelaySaveHelper?: DelaySaveHelper,
    keepLocationChangeRecord?: boolean
  ): Promise<DelaySaveHelper> {// initialise delayData if needed
    let delaySaveHelper = existingDelaySaveHelper || new DelaySaveHelper();
    delaySaveHelper.dailyLogIdsToDelete = delaySaveHelper.dailyLogIdsToDelete || [];
    delaySaveHelper.operationIdsToDelete = delaySaveHelper.operationIdsToDelete || [];
    delaySaveHelper.cancelledOperations = delaySaveHelper.cancelledOperations || [];

    // split step if cancellation time falls in step span
    await this.stepService.splitStepsAtTime(operationToCancel, delay.startTime, null, delay);

    // if delay is open ended, set endOfDay as end time
    let delayEnd = delay.endTime;

    if (!delay.endTime || !delay.endTime.dateTime) {
      const ptDate = await this.dailyLogService.getCurrentLogDay();
      const endOfDay = await this.dateService.getEndOfDayLocally(ptDate.dateTime);

      if (endOfDay.dateTime) {
        delayEnd = endOfDay;
      }
    }

    // if operation to cancel starts in the delay, then that operation will get deleted instead of cancelled
    const operationStartsInDelay = this.operationService.operationInTime(
      delay.startTime.dateTime,
      delayEnd.dateTime,
      operationToCancel.startTime.dateTime,
      operationToCancel.startTime.dateTime,
    );

    // Remove location change records for operation if it exists
    const operationLogs = await this.appDataSyncService.getLogsForOperation(operationToCancel.id);
    const locationLog = operationLogs.find(l => l.typeId === DailyLogType.LocationChange);

    if (locationLog && !keepLocationChangeRecord) {
      delaySaveHelper.dailyLogIdsToDelete.push(locationLog.id ? locationLog.id : locationLog.localId);
    }

    // if operation starts in delay, remove all daily log records, might get duplicate id of location change record above but it doesn't matter
    if (operationStartsInDelay) {
      for (const log of operationLogs) {
        delaySaveHelper.dailyLogIdsToDelete.push(log.id ? log.id : log.localId);
      }

      delaySaveHelper.operationIdsToDelete.push(operationToCancel.id ? operationToCancel.id : operationToCancel.localId);
    }

    // Update cancelled operation
    operationToCancel.cancelledTime = delay.startTime.dateTime > operationToCancel.startTime.dateTime
      ? cloneDeep(delay.startTime.dateTime)
      : operationToCancel.startTime.dateTime;
    operationToCancel.cancelledTimeOffset = delay.startTime.dateTime > operationToCancel.startTime.dateTime
      ? delay.startTimeOffset
      : operationToCancel.startTimeOffset;
    operationToCancel.newLocationId = null;
    operationToCancel.cancelId = delay.id;
    operationToCancel.delayLinkTypeId = DelayLinkType.Cancelled;

    delaySaveHelper.cancelledOperations.push(operationToCancel);

    delaySaveHelper = await this.saveAffectedOperationDailyLogRecordLogs(operationToCancel, delaySaveHelper);

    return delaySaveHelper;
  }

  async saveAffectedOperationDailyLogRecordLogs(operationInstance: DailyLogOperationInstance, existingDelaySaveHelper: DelaySaveHelper): Promise<DelaySaveHelper> {
    if (existingDelaySaveHelper.affectedOperationDailyLogRecords == null) {
      existingDelaySaveHelper.affectedOperationDailyLogRecords = [];
    }

    const steps = !operationInstance.stepCollectionInstance ? [] : operationInstance.stepCollectionInstance.stepInstances;
    const oldLogs = await this.dailyLogService.getDailyLogDataForOperation(operationInstance.id);
    const instanceLogs = await this.operationSaveService.saveOperation(cloneDeep(operationInstance), steps, oldLogs, cloneDeep(operationInstance), false);

    existingDelaySaveHelper.affectedOperationDailyLogRecords = existingDelaySaveHelper.affectedOperationDailyLogRecords.concat(instanceLogs);

    return existingDelaySaveHelper;
  }

  public async pauseOperation(
    delay: DailyLogOperationInstance,
    operationToPause: DailyLogOperationInstance,
    dailyLogDate: PtLuxonDate,
    existingDelayHelper?: DelaySaveHelper,
    originalDelayInstanceCopy?: DailyLogOperationInstance
  ): Promise<DelaySaveHelper> {
    // initialise delayData if needed
    let delaySaveHelper = existingDelayHelper || new DelaySaveHelper();
    delaySaveHelper.dailyLogIdsToDelete = delaySaveHelper.dailyLogIdsToDelete || [];
    delaySaveHelper.operationIdsToDelete = delaySaveHelper.operationIdsToDelete || [];
    delaySaveHelper.pausedOperations = delaySaveHelper.pausedOperations || [];

    const stepCountBeforeReverting = operationToPause.stepCollectionInstance && operationToPause.stepCollectionInstance.stepInstances
      ? operationToPause.stepCollectionInstance.stepInstances.length
      : 0;

    operationToPause = await this.prepareOperationToPause(cloneDeep(operationToPause), delay);

    delaySaveHelper.dailyLogIdsToDelete
      .concat(await this.stepService.revertPreviouslyAddedPause(operationToPause, delay, originalDelayInstanceCopy, delaySaveHelper));

    const revertedStepCount = operationToPause.stepCollectionInstance && operationToPause.stepCollectionInstance.stepInstances
      ? operationToPause.stepCollectionInstance.stepInstances.length
      : 0;

    // split step if delay falls in step span
    await this.stepService.splitStepsAtTime(operationToPause, delay.startTime, null, delay, stepCountBeforeReverting !== revertedStepCount, originalDelayInstanceCopy);

    // if step count is not different after running the above split, then it means we need to manually move step timings on in the below handle methods
    const stepCountAfterSplit = operationToPause.stepCollectionInstance && operationToPause.stepCollectionInstance.stepInstances
      ? operationToPause.stepCollectionInstance.stepInstances.length
      : 0;

    const operationStartsInDelay = this.operationService.operationInTime(
      delay.startTime.dateTime,
      delay.endTime.dateTime,
      operationToPause.startTime.dateTime,
      operationToPause.startTime.dateTime,
    );

    // if op starts in delay, it gets moved until after the delay finishes
    // otherwise, handle delay pause here
    if (operationStartsInDelay) {
      delaySaveHelper = await this.handleOperationStartDelayPause(delay, operationToPause, delaySaveHelper);
    } else {
      if (delay.endTime.dateTime == null || isNaN(delay.endTime.dateTime.toMillis()) || !delay.endTimeToShow) {
        delaySaveHelper = await this.handleOpenEndedDelayPause(delay, operationToPause, delaySaveHelper, originalDelayInstanceCopy);
      } else {
        // step timings get adjusted here
        delaySaveHelper = await this.handleStandardDelayPause(delay, operationToPause, delaySaveHelper, originalDelayInstanceCopy, dailyLogDate, stepCountAfterSplit === revertedStepCount);
      }
    }

    // after applying the pause, if user has edited a previously added delay, we may need to re-merge some steps due to the pause creating
    // another step around the delay - but not removing the old one. Exception to this is if we are re-adding an end time to a delay that is open ended
    // if we re-join here it will end up setting a null end time to the first half of the split step, as the other half has a null start
    delaySaveHelper.dailyLogIdsToDelete = delaySaveHelper.dailyLogIdsToDelete.concat(await this.stepService.attemptJoinStepsInOperation(operationToPause));

    delaySaveHelper = await this.saveAffectedOperationDailyLogRecordLogs(operationToPause, delaySaveHelper);

    return delaySaveHelper;
  }

  private async handleOperationStartDelayPause(
    delay: DailyLogOperationInstance,
    operation: DailyLogOperationInstance,
    delaySaveHelper: DelaySaveHelper
  ): Promise<DelaySaveHelper> {
    if (!operation) {
      return;
    }

    // Calculate the duration of this delay in milliseconds
    let delayDuration = 0;
    if (!!delay.endTime.getDate()) {
      delayDuration = delay.endTime.dateTime.toMillis() - delay.startTime.dateTime.toMillis();
    }

    if (delay.startTime.dateTime.toMillis() <= operation.startTime.dateTime.toMillis()) {
      delayDuration = 0;
    }

    // Calculate how long the operation and steps need to be shifted by
    // Equals: DelayDuration + (difference between delay end and instance start)
    const shuntTime = (delay.endTime.dateTime.toMillis() - operation.startTime.dateTime.toMillis()) + delayDuration;

    // The new Operation Start Date should be when the Delay ends
    const newOperationStartDate = operation.startTime.dateTime.plus({millisecond: shuntTime});
    operation.startConfirmed = false;
    operation.startTime.setDate(newOperationStartDate);
    operation.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.startTime);

    // if operation has a transiting record attached, get the record and move it too
    if (operation.previousLocationId) {
      const linkedTransitRecords = await this.transitingSaveService.updateLinkedTransitingToEntry(cloneDeep(operation));

      if (linkedTransitRecords) {
        delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(linkedTransitRecords.setupChangeLogRecords);
        delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(linkedTransitRecords.dailyLogRecords);
      }
    }

    // If the operation has an End Time, then this should be shifted
    if (!!operation.endTime.dateTime && !isNaN(operation.endTime.dateTime.toMillis())) {
      const newEndDate = operation.endTime.dateTime.plus({millisecond: shuntTime});
      operation.endConfirmed = false;
      operation.endTime.setDate(newEndDate);
      operation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.endTime);

      // if operation has a location change, get the record and move it too
      if (operation.newLocationId) {
        const linkedLocationRecords = await this.transitingSaveService.updateLinkedLocationChangeEntry(cloneDeep(operation));

        if (linkedLocationRecords) {
          delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(linkedLocationRecords.setupChangeLogRecords);
          delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(linkedLocationRecords.dailyLogRecords);
        }
      }
    }

    const steps = !operation.stepCollectionInstance ? null : operation.stepCollectionInstance.stepInstances;
    // If this operation has Steps then these need to be shifted also
    if (!!steps) {
      for (const step of steps) {

        // If the step does not have a start time, don't shift dates
        if (!step.startTime.dateTime || isNaN(step.startTime.dateTime.toMillis())) {
          continue;
        }

        // If the step starts after the delay start time then
        // the step start/end time should be shifted
        if (step.startTime.dateTime >= delay.startTime.getDate()) {
          const newStartDate = step.startTime.dateTime.plus({millisecond: shuntTime});

          step.startTime.setDate(newStartDate);
          step.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
          step.startConfirmed = false;

          // If the step does not have end time, don't shift end date
          if (!step.endTime.dateTime || isNaN(step.endTime.dateTime.toMillis() + shuntTime)) {
            continue;
          }

          const newEndDate = step.endTime.dateTime.plus({millisecond: shuntTime});
          step.endTime.setDate(newEndDate);
          step.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
          step.endConfirmed = false;
        }
      }
    }

    operation.delayLinkTypeId = DelayLinkType.Paused;
    delaySaveHelper.pausedOperations.push(operation);

    return delaySaveHelper;
  }

  private async handleStandardDelayPause(
    delay: DailyLogOperationInstance,
    operation: DailyLogOperationInstance,
    delaySaveHelper: DelaySaveHelper,
    originalDelayCopy: DailyLogOperationInstance,
    dailyLogDate: PtLuxonDate,
    adjustStepTimes?: boolean
  ): Promise<DelaySaveHelper> {
    if (!operation) {
      return;
    }

    // Calculate the duration of this delay in milliseconds
    const originalDelayDuration = originalDelayCopy && originalDelayCopy.endTime && originalDelayCopy.endTime.dateTime
      ? originalDelayCopy.endTime.dateTime.toMillis() - originalDelayCopy.startTime.dateTime.toMillis()
      : 0;
    const delayDuration = delay.endTime.dateTime.toMillis() - delay.startTime.dateTime.toMillis();
    let delayDurationDifference = delayDuration - originalDelayDuration;

    const pause = new TimeDuration();
    pause.startDate = cloneDeep(delay.startTime);
    pause.startDateOffset = cloneDeep(delay.startTimeOffset);
    pause.endDate = cloneDeep(delay.endTime);
    pause.endDateOffset = cloneDeep(delay.endTimeOffset);
    pause.delayApiId = delay.id;
    pause.id = this.calculatePauseId(operation.pausedTimes, originalDelayCopy);

    const steps = !operation.stepCollectionInstance ? null : operation.stepCollectionInstance.stepInstances;

    let oldPause = operation.pausedTimes.find(p => p.delayApiId === delay.id);

    if (!!oldPause) {
      oldPause = await this.dateParserService.deserializeDate(oldPause);
      oldPause.startDate = PtLuxonDate.assignPtLuxonDate(oldPause.startDate);
      oldPause.endDate = PtLuxonDate.assignPtLuxonDate(oldPause.endDate);

      const oldDelayDuration =
        oldPause.endDate.dateTime != null && oldPause.startDate.dateTime != null ?
          oldPause.endDate.dateTime.toMillis() - oldPause.startDate.dateTime.toMillis() :
          0;

      if (!!steps) {
        for (const step of steps) {
          // If the step does not have a start time, don't revert dates
          if (!step.startTime.dateTime || isNaN(step.startTime.dateTime.toMillis())) {
            continue;
          }

          // Revert start time
          if (step.startTime.dateTime >= oldPause.startDate.getDate()) {
            const newStartDate = step.startTime.dateTime.minus({millisecond: oldDelayDuration});
            step.startTime.setDate(newStartDate);
            step.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
            step.startConfirmed = false;

            // If the step does not have end time, don't revert end date
            if (!step.endTime.dateTime || isNaN(step.endTime.dateTime.toMillis() - oldDelayDuration)) {
              continue;
            }

            // Revert end time
            const newEndDate = step.endTime.dateTime.minus({millisecond: oldDelayDuration});
            step.endTime.setDate(newEndDate);
            step.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
            step.endConfirmed = false;
          }
        }
      }

      pause.id = oldPause.id;
    }

    operation.pausedTimes = operation.pausedTimes.filter(p => p.delayApiId !== delay.id);
    operation.pausedTimes.push(pause);

    if (delay.startTime.dateTime.toMillis() <= operation.startTime.dateTime.toMillis()) {
      delayDurationDifference = 0;
    }

    // If the delay falls within a step time, then we don't need to move step times here, as they would have been moved when the step was split earlier.
    // However if the delay falls in between steps, or falls where there are point in time steps (then adjustSteps === true), we need to move them forward here.
    if (adjustStepTimes) {
      if (steps) {
        let delayDurationAdded = false;
        for (let i = 0; i < steps.length; i++) {
          // don't re-add step timings if the steps have been nulled
          if (!steps[i].exclude && (!steps[i].startTime || !steps[i].startTime.dateTime)) {
            continue;
          }

          // If the step does not have a start time, (aka it was nulled previously after adding an open ended delay), then we need to use the default
          // delayed entry time, and work out what the start should be from the previous step
          if (!steps[i].startTime.dateTime || isNaN(steps[i].startTime.dateTime.toMillis())) {
            const previousStep = i > 0 ? steps[i - 1] : null;

            if (previousStep == null && i !== 0) {
              continue;
            }

            let mostRecentDateTime: DateTime = previousStep && previousStep.endTime && previousStep.endTime.dateTime
              ? previousStep.endTime.dateTime
              : previousStep && previousStep.startTime && previousStep.startTime.dateTime
                ? previousStep.startTime.dateTime
                : operation.startTime.dateTime;

            if (mostRecentDateTime < dailyLogDate.dateTime) {
              mostRecentDateTime = delay.startTime && delay.startTime.dateTime ? delay.startTime.dateTime : dailyLogDate.dateTime;
            }

            // only add delay duration for the first step, we don't want to add it to every step, as subsequent steps are based off of the previous step time anyway
            const newStartDate = mostRecentDateTime.plus({millisecond: (delayDurationAdded ? 0 : delayDurationDifference) + (steps[i].step.defaultSecondsDelay * 1000)});
            delayDurationAdded = true;
            steps[i].startTime.setDate(newStartDate);

            steps[i].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[i].startTime);
            steps[i].startConfirmed = false;

            const newEndDate = steps[i].startTime.dateTime.plus( (steps[i].step.defaultSecondsSpan * 1000));
            steps[i].endTime.setDate(newEndDate);
            steps[i].endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[i].endTime);
            steps[i].endConfirmed = false;
          } else if (steps[i].startTime.dateTime >= delay.startTime.getDate()) {
            // If the step starts after the delay start time then
            // the step start/end time should be shifted
            const newStartDate = steps[i].startTime.dateTime.plus({millisecond: delayDurationDifference});
            steps[i].startTime.setDate(newStartDate);
            steps[i].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[i].startTime);
            steps[i].startConfirmed = false;

            // If the step does not have end time, don't shift end date
            if (!steps[i].endTime.dateTime || isNaN(steps[i].endTime.dateTime.toMillis() + delayDurationDifference)) {
              continue;
            }

            const newEndDate = steps[i].endTime.dateTime.plus({millisecond: delayDurationDifference});
            steps[i].endTime.setDate(newEndDate);
            steps[i].endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[i].endTime);
            steps[i].endConfirmed = false;
          }
        }
      }

      // If the operation has an End Time, then this should be shifted
      if (!!operation.endTime.dateTime && !isNaN(operation.endTime.dateTime.toMillis())) {
        const newEndDate = operation.endTime.dateTime.plus({millisecond: delayDurationDifference});
        operation.endConfirmed = false;
        operation.endTime.setDate(newEndDate);
        operation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.endTime);

        // if operation has a location change, get the record and move it too
        if (operation.newLocationId) {
          const linkedLocationRecords = await this.transitingSaveService.updateLinkedLocationChangeEntry(cloneDeep(operation));

          if (linkedLocationRecords) {
            delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(linkedLocationRecords.setupChangeLogRecords);
            delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(linkedLocationRecords.dailyLogRecords);
          }
        }
      } else {
        const lastStep = operation.stepCollectionInstance && operation.stepCollectionInstance.stepInstances && operation.stepCollectionInstance.stepInstances.length > 0
          ? operation.stepCollectionInstance.stepInstances[operation.stepCollectionInstance.stepInstances.length - 1]
          : null;

        if (lastStep && lastStep.endTime && lastStep.endTime.getDate()) {
          let newEndDate: DateTime;

          // if lastStep < delay end, then set the op end to be 10 mins after delay end, else set it as last step
          if (lastStep.endTime.getDate() < delay.endTime.getDate()) {
            newEndDate = cloneDeep(delay.endTime.dateTime).plus({millisecond: 600000});
            const newDateTimeZone = await this.timeZoneService.getEditorLocally({dateTime: newEndDate, dateAmbiguous: false});
            operation.endTimeOffset = newDateTimeZone.secondsOffsetFromUtc !== 0 ? newDateTimeZone.secondsOffsetFromUtc / 3600 : 0;
          } else {
            newEndDate = cloneDeep(lastStep.endTime.dateTime);
            operation.endTimeOffset = lastStep.endTimeOffset;
          }

          operation.endConfirmed = false;
          operation.endTime.setDate(newEndDate);
          operation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.endTime);

          // if operation has a location change, get the record and move it too
          if (operation.newLocationId) {
            const linkedLocationRecords = await this.transitingSaveService.updateLinkedLocationChangeEntry(cloneDeep(operation));

            if (linkedLocationRecords) {
              delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(linkedLocationRecords.setupChangeLogRecords);
              delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(linkedLocationRecords.dailyLogRecords);
            }
          }
        } else if (!steps || (steps && steps.every(s => (s.startTime && s.startTime.dateTime) || s.exclude))) {
          // don't add the end time if we have steps but some steps are nulled
          // if no steps we create the new end date when adding a delay end time back to a previously nulled delay, by taking the original operation duration,
          // plus the delay duration, exception to this would be if the user has previously edited OP times so that these may no longer apply. If this is the
          // case then we just add a check to ensure the new end time is later than the pause end, and if not just set the end to equal the pause end + 10 mins
          // and the user will have to manually correct this
          // - there's no alternative that wouldn't take too long to implement I.e remembering op duration before delay added
          const operationDefaultDuration = operation.projectSpecificOperation.operationSummary.estimatedDurationSeconds;
          let newEndDate = operation.startTime.dateTime.plus({millisecond: delayDurationDifference + (operationDefaultDuration * 1000)});

          if (newEndDate <= pause.endDate.dateTime) {
            newEndDate = pause.endDate.dateTime.plus({millisecond: 600000});
          }

          operation.endConfirmed = false;
          operation.endTime.setDate(newEndDate);
          const newDateTimeZone = await this.timeZoneService.getEditorLocally({dateTime: newEndDate, dateAmbiguous: false});
          operation.endTimeOffset = newDateTimeZone.secondsOffsetFromUtc !== 0 ? newDateTimeZone.secondsOffsetFromUtc / 3600 : 0;
          operation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.endTime);

          // if operation has a location change, get the record and move it too
          if (operation.newLocationId) {
            const linkedLocationRecords = await this.transitingSaveService.updateLinkedLocationChangeEntry(cloneDeep(operation));

            if (linkedLocationRecords) {
              delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(linkedLocationRecords.setupChangeLogRecords);
              delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(linkedLocationRecords.dailyLogRecords);
            }
          }
        }
      }
    } else {
      // TODO: If SJ want to correct step times for split step + subsequent steps after re-adding an end time to a delay that was previously open ended
      // TODO: then we will need to do it here, so leaving this todo, plus comment to make it easier
      // if !adjustStepTimes, then steps would have been split earlier in "await this.stepService.splitStepsAtTime()"
      // normally we don't need to do anything here, but if user is adding an end time back to a delay after it previously being open ended
      // then the split step second half will have a null end time. We want to correct this here by adding an end time to this plus set subsequent step times.
    }

    operation.delayLinkTypeId = DelayLinkType.Paused;
    delaySaveHelper.pausedOperations.push(operation);

    return delaySaveHelper;
  }

  private async handleOpenEndedDelayPause(
    delay: DailyLogOperationInstance,
    operation: DailyLogOperationInstance,
    delaySaveHelper: DelaySaveHelper,
    originalDelayCopy: DailyLogOperationInstance
  ): Promise<DelaySaveHelper> {
    const pause = new TimeDuration();
    pause.startDate = cloneDeep(delay.startTime);
    pause.startDateOffset = cloneDeep(delay.startTimeOffset);
    pause.endDate = cloneDeep(delay.endTime);
    pause.endDateOffset = cloneDeep(delay.endTimeOffset);
    pause.endDate.setDate(null);
    pause.delayApiId = delay.id;
    pause.id = this.calculatePauseId(operation.pausedTimes, originalDelayCopy);

    const oldPause = operation.pausedTimes.find(p => p.delayApiId === delay.id);

    if (!!oldPause) {
      pause.id = oldPause.id;
    }

    operation.pausedTimes = operation.pausedTimes.filter(p => p.delayApiId !== delay.id);
    operation.pausedTimes.push(pause);

    operation.endConfirmed = false;
    operation.endTime.setDate(null);
    operation.endTimeToShow = null;

    const steps = !operation.stepCollectionInstance ? null : operation.stepCollectionInstance.stepInstances;

    if (!!steps) {
      for (const step of steps) {
        // If the step does not have a start time, don't shift dates
        if (!step.startTime.dateTime || isNaN(step.startTime.dateTime.toMillis())) {
          continue;
        }
        // If the step starts after the delay start time then the step start time should be nulled
        if (step.startTime.dateTime >= delay.startTime.getDate()) {
          step.startTime.setDate(null);
          step.startTimeToShow = null;
          step.startConfirmed = false;
        }

        // If the step does not have a start time, don't shift dates
        if (!step.endTime.dateTime || isNaN(step.endTime.dateTime.toMillis())) {
          continue;
        }
        // If the step ends after the delay start time then step end time should be nulled
        if (step.endTime.dateTime > delay.startTime.getDate()) {
          step.endTime.setDate(null);
          step.endTimeToShow = null;
          step.endConfirmed = false;
        }
      }
    }

    // Remove location change records for operation
    const operationDailyLogRecords = await this.appDataSyncService.getLogsForOperation(operation.id);
    const locationLog = operationDailyLogRecords.find(l => l.typeId === DailyLogType.LocationChange);

    if (locationLog) {
      delaySaveHelper.dailyLogIdsToDelete.push(locationLog.id);
    }

    operation.delayLinkTypeId = DelayLinkType.Paused;
    delaySaveHelper.pausedOperations.push(operation);

    return delaySaveHelper;
  }

  private async checkThirdStepSplitNeeded(operationToSwitch: DailyLogOperationInstance) {
    // we call this again here - once we have added the above switch to the operation - if that switch then intersects with
    // a step that has already been split into 2 by the delay start - then it will get split again into 3, e.g - the
    // step starts as contractual - then is split above into 2 by the delay - but if the step needs to then return to concurrent after
    // the delay ends - then this is where that will happen
    for (let i = 0; i < operationToSwitch.switchTimes.length; i++) {
      const element = operationToSwitch.switchTimes[i];
      await this.stepService.splitStepsAtTime(operationToSwitch, element.date);
    }

    // we need to check if any steps can be stitched together after the above splitting - this can occur if the delay times have been changed
    // if the delay originally split the step into 3 - and then the user changes the delay timings - then the above will end up splitting steps into
    // 4 - so this call will stitch any steps back together where possible
    await this.stepService.attemptJoinStepsInOperation(operationToSwitch);
  }

  // checks operation for open-ended steps. If user is adding an end time to a delay that was previously open ended- then this method
  // will ensure that the open-ended step is given an end time, which will then result in the step being split in 2 (1 before and 1 after the delay)
  private async prepareOperationToPause(operationToPause: DailyLogOperationInstance, delay: DailyLogOperationInstance): Promise<DailyLogOperationInstance> {
    if (this.operationHasSteps(operationToPause)) {
      for (let i = 0; i < operationToPause.stepCollectionInstance.stepInstances.length; i++) {
         if (this.stepIsOpenEnded(operationToPause.stepCollectionInstance.stepInstances[i])
         && this.stepBeginsBeforeDelay(operationToPause.stepCollectionInstance.stepInstances[i], delay)) {
          operationToPause.stepCollectionInstance.stepInstances[i].endTime = cloneDeep(delay.startTime);
          operationToPause.stepCollectionInstance.stepInstances[i].endTimeOffset = cloneDeep(delay.startTimeOffset);
           operationToPause.stepCollectionInstance.stepInstances[i].endTime.dateTime =
             await this.dateService.addDuration(delay.startTime.dateTime, {minutes: 10});
           operationToPause.stepCollectionInstance.stepInstances[i].endTimeToShow =
             this.dateService.setTimeStringFromPtLuxonDate(operationToPause.stepCollectionInstance.stepInstances[i].endTime);

           break;
        }
      }
    }

    return operationToPause;
  }

  private operationHasSteps(operation: DailyLogOperationInstance): boolean {
    return operation.stepCollectionInstance && operation.stepCollectionInstance.stepInstances && operation.stepCollectionInstance.stepInstances.length > 0;
  }

  private stepIsOpenEnded(stepInstance: StepInstance): boolean {
    return this.timeExists(stepInstance.startTime) && !this.timeExists(stepInstance.endTime);
  }

  private timeExists(ptLuxonDate: PtLuxonDate): boolean {
    return ptLuxonDate && ptLuxonDate.dateTime !== null;
  }

  private stepBeginsBeforeDelay(stepInstance: StepInstance, delay: DailyLogOperationInstance) {
    return stepInstance.startTime && stepInstance.startTime.dateTime < delay.startTime.dateTime;
  }
}



// WEBPACK FOOTER //
// ./src/services/delay/delay-service/delay.service.ts